import React, { useState } from "react";
import {
  Box,
  Paper,
  Container,
  Tabs,
  Tab,
} from "@mui/material";
import PlaylistAddCheckSharpIcon from "@mui/icons-material/PlaylistAddCheckSharp";
import { StartGame } from "./Components/StartGame";
import { TabConfig } from "../../../types/GeneralTypes";
import { AllSideTabs } from "../../../constants";

import { useLogger } from "../../../contexts/LoggerContext";
import { LogTable } from "./Components/LogTable";
import { Side } from "../../../types/DepthChartTypes";
import { InGameStats } from "./Components/InGameStats";
import { SavePlayDialog } from "./SavePlay/SavePlayDialog";
import FieldGoalLogger from "./FieldGoal/FieldGoalLogger";
import { DefenseLogger } from "./Defense/DefenseLogger";
import { OffenseLogger } from "./Offense/OffenseLogger";
import { KickOffLogger } from "./KickOff/KickOffLogger";
import { PuntLogger } from "./Punt/PuntLogger";
import { KickOffReturnLogger } from "./KickOffReturn/KickOffReturnLogger";
import { PuntReturnLogger } from "./PuntReturn/PuntReturnLogger";
import { FieldGoalBlockLogger } from "./FieldGoalBlock/FieldGoalBlockLogger";
import { Scoreboard } from "./Components/Scoreboard";
import { EditScoreBoardDialog } from "./Components/EditScoreBoardDialog";

interface ModalState {
  showLogTable: boolean;
  showInGameStats: boolean;
  editScoreboardDialogOpen: boolean;
  savePlayDialogOpen: boolean;
}

export const GameLogger = () => {
  const {
    gameStarted,
    selectedTab,
    selectedSide,
    setSelectedTab,
    setSelectedSide,
  } = useLogger();

  const [modalState, setModalState] = useState<ModalState>({
    showLogTable: false,
    showInGameStats: false,
    editScoreboardDialogOpen: false,
    savePlayDialogOpen: false,
  });

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
    setSelectedSide(AllSideTabs[newValue].value as Side);
  };

  const handleLogTableToggle = () => {
    setModalState({
      showLogTable: !modalState.showLogTable,
      showInGameStats: false,
      editScoreboardDialogOpen: false,
      savePlayDialogOpen: false,
    });
  };

  const handleInGameStatsToggle = () => {
    setModalState({
      showLogTable: false,
      showInGameStats: !modalState.showInGameStats,
      editScoreboardDialogOpen: false,
      savePlayDialogOpen: false,
    });
  };

  const handleEditScoreboard = () => {
    setModalState({
      showLogTable: false,
      showInGameStats: false,
      editScoreboardDialogOpen: !modalState.editScoreboardDialogOpen,
      savePlayDialogOpen: false,
    });
  };

  const handleSavePlayDialogOpen = () => {
    setModalState({
      showLogTable: false,
      showInGameStats: false,
      editScoreboardDialogOpen: false,
      savePlayDialogOpen: true,
    });
  };

  return (
    <Container
      sx={{
        mt: 2,
        mb: 4,
        maxWidth: 1275,
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: 1,
          borderRadius: 2,
          minHeight: "calc(100vh - 150px)",
          width: "100%",
        }}
      >
        {/* Game Logger */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {/* Scoreboard */}
          <Scoreboard
            handleLogTableToggle={handleLogTableToggle}
            handleInGameStatsToggle={handleInGameStatsToggle}
            handleSavePlayDialogOpen={handleSavePlayDialogOpen}
            handleEditScoreboard={handleEditScoreboard}
          />

          {/* Main Content */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              flex: 1,
            }}
          >
            {/* Game Started Logic */}
            {gameStarted ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  borderBottom: 1,
                  borderColor: "divider",
                  flexDirection: "column",
                  width: "100%",
                  flex: 1,
                }}
              >
                {/* Sides Tabs and Save Play Log Button */}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    width: "100%",
                    flex: 1,
                  }}
                >
                  {/* Sides Tabs */}
                  <Box sx={{ flex: 1 }}>
                    <Tabs
                      value={selectedTab}
                      onChange={handleTabChange}
                      variant="scrollable"
                      scrollButtons="auto"
                      sx={{
                        "& .MuiTabs-indicator": {
                          backgroundColor: "rgba(123, 45, 58, 0.9)",
                        },
                        "& .MuiTab-root": {
                          color: "rgba(255, 255, 255, 0.7)",
                          "&.Mui-selected": {
                            color: "white",
                          },
                        },
                      }}
                    >
                      {AllSideTabs.map((tab: TabConfig, index: number) => (
                        <Tab
                          key={tab.value}
                          label={tab.label}
                          id={`game-logger-tab-${index}`}
                        />
                      ))}
                    </Tabs>
                  </Box>
                  {/* <Box sx={{ justifyContent: "flex-end", mr: 1 }}>
                    <Tooltip title="Save Play Log">
                      <IconButton onClick={() => setSavePlayDialogOpen(true)}>
                        <PlaylistAddCheckSharpIcon fontSize="large" />
                      </IconButton>
                    </Tooltip>
                  </Box> */}
                </Box>

                {/* Game Logging Content */}
                {selectedSide === "OFFENSE" && <OffenseLogger />}
                {selectedSide === "DEFENSE" && <DefenseLogger />}
                {selectedSide === "KICKOFF" && <KickOffLogger />}
                {selectedSide === "KICKOFF_RETURN" && <KickOffReturnLogger />}
                {selectedSide === "PUNT" && <PuntLogger />}
                {selectedSide === "PUNT_RETURN" && <PuntReturnLogger />}
                {selectedSide === "FIELD_GOAL" && <FieldGoalLogger />}
                {selectedSide === "FIELD_GOAL_BLOCK" && (
                  <FieldGoalBlockLogger />
                )}
              </Box>
            ) : (
              <StartGame />
            )}
            <LogTable open={modalState.showLogTable} />
            <InGameStats open={modalState.showInGameStats} />
            <SavePlayDialog
              open={modalState.savePlayDialogOpen}
              onClose={() => setModalState({ ...modalState, savePlayDialogOpen: false })}
            />
            <EditScoreBoardDialog
              open={modalState.editScoreboardDialogOpen}
              onClose={() => setModalState({ ...modalState, editScoreboardDialogOpen: false })}
            />
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};
